/**
 * @file MemberToolbarPortal renders the MemberToolbar into specified non-React areas of the app.
 *
 * This portal is used to add the member toolbar to the following paths:
 * - `/user/updateField/<fieldID>/sure` — Update a specific field for the user.
 * - `/note/<noteID>` — Display details about a note.
 * - `/user/<userID>` — Show a user's profile.
 * - `/network/<networkID>/moregroups` — View additional groups in a network (logout_goes_to_login).
 * - `/network/<networkID>/confirm` — Confirms network signup (logout_goes_to_login).
 * - `/completeProfile` — Assist the user in completing their profile (logout_goes_to_login).
 * - `/me/createPassword` — Allow the user to create a new password (logout_goes_to_login).
 * - `/me/resetPassword` — Allow the user to reset their password (logout_goes_to_login).
 *
 * The toolbar is injected into a non-React container identified by the class
 * `.member_toolbar_react_holder`. To hide the logout button, add the class
 * `logout_goes_to_login` to the container as well.
 */
import { useMemo } from "react";
import { Route, useLocation } from "react-router-dom";

import styled, { css } from "styled-components";

import Portal from "Utilities/PortalMaker";
import { usePortalElement } from "Utilities/PortalMaker/usePortalElement";

import { MemberToolbar } from "./MemberToolbar";

const MemberToolbarPortalUnrouted = () => {
  const location = useLocation();
  const pathname = useMemo(() => location?.pathname, [location?.pathname]);

  const portalElement = usePortalElement(".member_toolbar_react_holder");

  if (!portalElement) return null; // non-React area hasn't loaded yet

  return (
    <Portal to={portalElement}>
      <SMemberToolbar
        loginVariant="mmg"
        logoutGoesToLogin={portalElement.className.includes(
          "logout_goes_to_login"
        )}
        contextInquiryText={portalElement.dataset.contextInquiryText}
        $pathname={pathname}
      />
    </Portal>
  );
};

const MemberToolbarPortal = () => {
  return (
    <Route
      path={["/note/:noteID", "*"]}
      component={MemberToolbarPortalUnrouted}
    />
  );
};

const notePageRegExp = new RegExp("/note/\\d+");
const notePageExtraCss = css`
  @media screen and (min-width: 1281px) {
    left: calc(50% - 210px);
  }
  @media screen and (min-width: 1181px) and (max-width: 1280px) {
    left: 431px;
  }
  @media screen and (min-width: 1061px) and (max-width: 1180px) {
    left: 36.5%;
  }
  @media screen and (min-width: 1000px) and (max-width: 1060px) {
    left: 371px;
  }
  @media screen and (min-width: 881px) and (max-width: 1000px) {
    left: calc(26% + 1px);
  }
  @media screen and (min-width: 841px) and (max-width: 880px) {
    left: 25%;
  }
  @media screen and (min-width: 701px) and (max-width: 840px) {
    left: 210px;
  }
`;

const styleAdjustmentsByPathname = ({ $pathname }) => {
  if (notePageRegExp.test($pathname)) return notePageExtraCss;
};

const SMemberToolbar = styled(MemberToolbar)`
  ${styleAdjustmentsByPathname}
`;

export { MemberToolbarPortal };
